import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ConfigService } from '../../services/config.service';
import { localStorageKeys } from '../constants/local.storage.keys';
import { bindFunctionsToThis, isNullOrUndefined } from '../extensions';
import { ApiErrorHandler } from './api.error.handler';

@Injectable()
export class ApiService {
    private headers = new HttpHeaders();

    constructor(protected http: HttpClient,
                protected apiErrorHandler: ApiErrorHandler) {
        bindFunctionsToThis(this);

        let authToken;
        try {
            authToken = localStorage.getItem(localStorageKeys.authToken);
        } catch (error) {
            if (error && error.message.indexOf('localStorage') >= 0) {
                alert('Your chrome is set to block 3rd party cookie in incognito mode, you can change this under Settings > Privacy > Content settings, change the cookies\' settings to "Allow local data to be set" and refresh the page');
            }
        }

        if (!!authToken) {
            this.headers = this.headers.set('Authorization', authToken);
        }
    }

    public get(url, paramsObj?, options = new ApiRequestOptions()): Observable<any> {
        let params = ApiService.getParamsFromObject(paramsObj);
        return this.http.get(`${ConfigService.config.apiAddress}${url}`, {params, headers: this.headers})
            .timeout(options.timeout)
            .map(response => ApiService.parseResponse(response, options))
            .share()
            .catch(this.handleError);
    }

    public put(url, body?, options = new ApiRequestOptions()): Observable<any> {
        return this.http.put(`${ConfigService.config.apiAddress}${url}`, body, {headers: this.headers})
            .timeout(options.timeout)
            .map(response => ApiService.parseResponse(response, options))
            .share()
            .catch(this.handleError);
    }

    public post(url, body?, options = new ApiRequestOptions()): Observable<any> {
        return this.http.post(`${ConfigService.config.apiAddress}${url}`, body, {headers: this.headers})
            .timeout(options.timeout)
            .map(response => ApiService.parseResponse(response, options))
            .share()
            .catch(this.handleError);
    }

    public delete(url, paramsObj?, options = new ApiRequestOptions()): Observable<any> {
        let params = ApiService.getParamsFromObject(paramsObj);

        return this.http.delete(`${ConfigService.config.apiAddress}${url}`, {params, headers: this.headers})
            .timeout(options.timeout)
            .share()
            .catch(this.handleError);
    }

    public setAuthorizationHeader(token: string) {
        let authToken = `Bearer ${token}`;

        this.headers = this.headers.set('Authorization', authToken);
        localStorage.setItem(localStorageKeys.authToken, authToken);
    }

    public clearAuthorizationHeader() {
        this.headers = this.headers.delete('Authorization');
        localStorage.removeItem(localStorageKeys.authToken);
    }

    private handleError(error) {
        this.apiErrorHandler.handle(error);
        return Observable.throw(error);
    }

    private static parseResponse(response, options: ApiRequestOptions): any {
        if (!response) {
            return response;
        }

        return options.isJson && response.hasOwnProperty('_body')
            ? response['_body'] ? response.json() : null
            : response;
    }

    private static getParamsFromObject(paramsObj: any) {
        let urlSearchParams = new HttpParams();

        if (paramsObj) {
            for (let key in paramsObj) {
                if (paramsObj.hasOwnProperty(key)) {
                    let paramValue = paramsObj[key];

                    if (paramValue instanceof Date) {
                        paramsObj[key] = paramsObj[key].toISOString();
                    }

                    if (isNullOrUndefined(paramValue)) {
                        delete paramsObj[key];
                    } else {
                        urlSearchParams = urlSearchParams.set(key, paramsObj[key]);
                    }
                }
            }
        }

        return urlSearchParams;
    }
}

export class ApiRequestOptions {
    public isJson? = true;
    public timeout = 600 * 1000;
}


// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/common/services/api.service.ts